import { environment } from "@environment";
import { getAuthToken } from "@/features/authentication";
import type {
  BrandAiExtractRequest,
  BrandAiExtractResult,
  GenerateOutlineRequest,
  GenerateOutlineResult,
  GeneratePromptRequest,
  GeneratePromptResult,
  GenerateSlideRequest,
  GenerateSlideResult,
  GenerateSlidesLayoutsRequest,
  GenerateSlidesLayoutsResult,
  OutlineItem,
} from "@/pages/how-to-start/types/generation";

const OUTLINE_TIMEOUT_MS = 120000;
const LAYOUTS_TIMEOUT_MS = 60000;
const SLIDE_TIMEOUT_MS = 90000;
const PROMPT_TIMEOUT_MS = 30000;

// The prompt generator's system prompt is phrased as "Transform general document
// types into…" and shows every example wrapped in quotes, so the model sometimes
// echoes that scaffolding instead of returning the prompt alone. Strip a leading
// label and any wrapping quotes so the textarea gets just the prompt text.
//
// The label varies ("Transform into:", "Output:", "Refined Output:", "Here's the
// prompt:"), so match any short run of words ending in a colon — bounded, and
// only when what follows starts a quote or a new line, so a real prompt
// containing a colon ("Warning: this deck…") isn't truncated.
const PROMPT_LABEL =
  /^\s*(?:[a-z][a-z'’]*\s+){0,3}[a-z][a-z'’]*\s*:\s*(?=["“'\n]|$)/i;
const QUOTE_PAIRS = [
  ['"', '"'],
  ["“", "”"],
  ["'", "'"],
];

export const stripPromptScaffolding = (raw: string): string => {
  let text = raw.trim().replace(PROMPT_LABEL, "").trim();
  const isWrapped = QUOTE_PAIRS.some(
    ([open, close]) =>
      text.length > 1 && text.startsWith(open) && text.endsWith(close),
  );
  if (isWrapped) text = text.slice(1, -1).trim();
  return text;
};
const BRAND_AI_EXTRACT_TIMEOUT_MS = 60000;

// Parses a single SSE `data:` line into a done flag + chunk (or throws on error payload).
const parseSseDataLine = (
  rawLine: string,
): { done: boolean; chunk: string } => {
  const line = rawLine.trim();
  if (!line.startsWith("data:")) return { done: false, chunk: "" };
  const payloadText = line.slice(5).trim();
  if (payloadText.startsWith("[DONE")) return { done: true, chunk: "" };
  let payload: { chunk?: string; error?: string; details?: string };
  try {
    payload = JSON.parse(payloadText);
  } catch {
    // Empty or malformed frame (e.g. a keep-alive or a partial trailing
    // line) — skip it instead of killing the whole stream read.
    return { done: false, chunk: "" };
  }
  if (payload.error) throw new Error(payload.details || payload.error);
  return { done: false, chunk: payload.chunk ?? "" };
};

// Reads a Vercel AI SDK `streamObject` SSE body (`data: {chunk}` text-delta
// frames terminated by `data: [DONE, ms]`) to completion and returns the
// concatenated JSON text. Shared by every docpilot endpoint that streams.
// `onText` receives the accumulated text after each network read, letting
// callers surface partial results while the stream is still running.
const readSseStreamToText = async (
  response: Response,
  onText?: (accumulated: string) => void,
): Promise<string> => {
  if (!response.body) throw new Error("Response has no body");

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffered = "";
  let text = "";
  let finished = false;

  try {
    while (!finished) {
      const { value, done } = await reader.read();
      if (value) buffered += decoder.decode(value, { stream: true });

      const lines = buffered.split("\n");
      buffered = lines.pop() ?? "";

      for (const rawLine of lines) {
        const { done: isDone, chunk } = parseSseDataLine(rawLine);
        if (isDone) {
          finished = true;
          break;
        }
        text += chunk;
      }

      onText?.(text);
      if (done) finished = true;
    }

    buffered += decoder.decode();
    const { chunk: trailingChunk } = parseSseDataLine(buffered);
    text += trailingChunk;
  } finally {
    reader.releaseLock();
  }

  return text;
};

// POSTs `body` to a docpilot endpoint that streams its result as SSE, reads
// the stream to completion, and parses the reassembled text as JSON.
// `signal` is an optional external abort (e.g. leaving the generating step)
// wired alongside the internal per-call timeout.
const postStreamedJson = async <T>(
  path: string,
  body: unknown,
  timeoutMs: number,
  timeoutMessage: string,
  onText?: (accumulated: string) => void,
  signal?: AbortSignal,
): Promise<T> => {
  const token = await getAuthToken();
  if (!token) throw new Error("Not authenticated");

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  const onExternalAbort = () => controller.abort();
  signal?.addEventListener("abort", onExternalAbort);
  if (signal?.aborted) controller.abort();

  try {
    const response = await fetch(`${environment.backend}${path}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: token,
      },
      body: JSON.stringify(body),
      signal: controller.signal,
    });

    if (!response.ok || !response.body) {
      throw new Error(
        `Request to ${path} failed: ${response.status} ${response.statusText}`,
      );
    }

    const text = await readSseStreamToText(response, onText);
    if (!text) throw new Error(`${path} returned no data`);

    return JSON.parse(text) as T;
  } catch (e) {
    if (e instanceof Error && e.name === "AbortError") {
      // Caller cancelled — preserve AbortError so UI can ignore it.
      if (signal?.aborted) throw e;
      throw new Error(timeoutMessage);
    }
    throw e;
  } finally {
    clearTimeout(timeoutId);
    signal?.removeEventListener("abort", onExternalAbort);
  }
};

// Pulls every *complete* slide object out of a partially streamed outline
// JSON (`{..., "slides": [{...}, {...`) by brace-matching inside the slides
// array, so the UI can show slides while the model is still writing.
const extractPartialSlides = (text: string): OutlineItem[] => {
  const keyIndex = text.indexOf('"slides"');
  if (keyIndex === -1) return [];
  const arrayStart = text.indexOf("[", keyIndex);
  if (arrayStart === -1) return [];

  const items: OutlineItem[] = [];
  let depth = 0;
  let objectStart = -1;
  let inString = false;
  let escaped = false;

  for (let i = arrayStart + 1; i < text.length; i++) {
    const char = text[i];
    if (inString) {
      if (escaped) escaped = false;
      else if (char === "\\") escaped = true;
      else if (char === '"') inString = false;
      continue;
    }
    if (char === '"') {
      inString = true;
    } else if (char === "{") {
      if (depth === 0) objectStart = i;
      depth += 1;
    } else if (char === "}") {
      depth -= 1;
      if (depth === 0 && objectStart !== -1) {
        try {
          const parsed = JSON.parse(
            text.slice(objectStart, i + 1),
          ) as Partial<OutlineItem>;
          items.push({
            internalName:
              typeof parsed.internalName === "string"
                ? parsed.internalName
                : `slide-${items.length}`,
            title: typeof parsed.title === "string" ? parsed.title : "",
            content: Array.isArray(parsed.content)
              ? parsed.content.filter((c): c is string => typeof c === "string")
              : [],
            dataPoints: Array.isArray(parsed.dataPoints)
              ? parsed.dataPoints.filter(
                  (d): d is string => typeof d === "string",
                )
              : [],
          });
        } catch {
          // Malformed mid-stream object — skip it; the final full parse governs.
        }
        objectStart = -1;
      }
    } else if (char === "]" && depth === 0) {
      break;
    }
  }
  return items;
};

export const docpilotApi = {
  generateOutline: (
    body: GenerateOutlineRequest,
    onPartialSlides?: (slides: OutlineItem[]) => void,
    signal?: AbortSignal,
  ): Promise<GenerateOutlineResult> => {
    // Re-parse the accumulated text on each network read, but only notify
    // when another slide has actually completed.
    let emitted = 0;
    const onText = onPartialSlides
      ? (accumulated: string) => {
          const slides = extractPartialSlides(accumulated);
          if (slides.length > emitted) {
            emitted = slides.length;
            onPartialSlides(slides);
          }
        }
      : undefined;
    return postStreamedJson<GenerateOutlineResult>(
      "/docpilot/generate-outline",
      body,
      OUTLINE_TIMEOUT_MS,
      "Outline generation timed out",
      onText,
      signal,
    );
  },

  // Maps the edited outline to a design/master-slide per slide
  // (docpilot's generateSlidesLayoutsStream), also streamed as SSE.
  generateSlidesLayouts: (
    body: GenerateSlidesLayoutsRequest,
    signal?: AbortSignal,
  ): Promise<GenerateSlidesLayoutsResult> =>
    postStreamedJson<GenerateSlidesLayoutsResult>(
      "/docpilot/generate-slides-layouts",
      body,
      LAYOUTS_TIMEOUT_MS,
      "Slide layout generation timed out",
      undefined,
      signal,
    ),

  // Plain JSON POST (not streamed): generates one fully mapped slide.
  // `signal` is an optional external abort (e.g. the caller's slide pool
  // aborting the rest of the batch on a sibling failure) that's wired
  // alongside the internal per-call timeout.
  generateSlide: async (
    body: GenerateSlideRequest,
    signal?: AbortSignal,
  ): Promise<GenerateSlideResult> => {
    const token = await getAuthToken();
    if (!token) throw new Error("Not authenticated");

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), SLIDE_TIMEOUT_MS);
    const onExternalAbort = () => controller.abort();
    signal?.addEventListener("abort", onExternalAbort);
    // Match postStreamedJson/brandAiExtract: honor a signal that was
    // already aborted before this call started.
    if (signal?.aborted) controller.abort();

    try {
      const response = await fetch(
        `${environment.backend}/docpilot/generate-slide`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
          body: JSON.stringify(body),
          signal: controller.signal,
        },
      );

      if (!response.ok) {
        // The route's error body carries the server-side cause: { error, details }.
        let details = "";
        try {
          const errBody = (await response.json()) as {
            error?: string;
            details?: string;
          };
          details = errBody.details || errBody.error || "";
        } catch {
          // non-JSON error body
        }
        throw new Error(
          `Failed to generate slide: ${response.status}${details ? ` — ${details}` : ""}`,
        );
      }

      return (await response.json()) as GenerateSlideResult;
    } catch (e) {
      if (e instanceof Error && e.name === "AbortError") {
        if (signal?.aborted) throw e;
        throw new Error("Slide generation timed out");
      }
      throw e;
    } finally {
      clearTimeout(timeoutId);
      signal?.removeEventListener("abort", onExternalAbort);
    }
  },

  // Plain JSON POST: AI-writes a starter prompt from the doc type + website.
  // The response is cleaned before use — see stripPromptScaffolding.
  generatePrompt: async (body: GeneratePromptRequest): Promise<string> => {
    const token = await getAuthToken();
    if (!token) throw new Error("Not authenticated");

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), PROMPT_TIMEOUT_MS);

    try {
      const response = await fetch(
        `${environment.backend}/docpilot/generate-prompt`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
          body: JSON.stringify(body),
          signal: controller.signal,
        },
      );

      if (!response.ok) {
        throw new Error(`Failed to generate a prompt: ${response.status}`);
      }

      const result = (await response.json()) as GeneratePromptResult;
      const prompt = stripPromptScaffolding(result.response ?? "");
      if (!prompt) {
        throw new Error("Prompt generation returned an empty result");
      }
      return prompt;
    } catch (e) {
      if (e instanceof Error && e.name === "AbortError") {
        throw new Error("Prompt generation timed out");
      }
      throw e;
    } finally {
      clearTimeout(timeoutId);
    }
  },

  // Plain JSON POST: Brandfetch record → AI-picked palettes + Google fonts.
  brandAiExtract: async (
    body: BrandAiExtractRequest,
    signal?: AbortSignal,
  ): Promise<BrandAiExtractResult> => {
    const token = await getAuthToken();
    if (!token) throw new Error("Not authenticated");

    const controller = new AbortController();
    const timeoutId = setTimeout(
      () => controller.abort(),
      BRAND_AI_EXTRACT_TIMEOUT_MS,
    );
    const onExternalAbort = () => controller.abort();
    signal?.addEventListener("abort", onExternalAbort);
    if (signal?.aborted) controller.abort();

    try {
      const response = await fetch(
        `${environment.backend}/docpilot/brand-ai-extract`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
          body: JSON.stringify(body),
          signal: controller.signal,
        },
      );

      if (!response.ok) {
        let details = "";
        try {
          const errBody = (await response.json()) as {
            error?: string;
            details?: string;
          };
          details = errBody.details || errBody.error || "";
        } catch {
          // non-JSON error body
        }
        throw new Error(
          `Failed to extract brand design: ${response.status}${details ? ` — ${details}` : ""}`,
        );
      }

      return (await response.json()) as BrandAiExtractResult;
    } catch (e) {
      if (e instanceof Error && e.name === "AbortError") {
        // Caller cancellation stays an AbortError; only our own timer is a timeout.
        if (signal?.aborted) throw e;
        throw new Error("Brand design extraction timed out");
      }
      throw e;
    } finally {
      clearTimeout(timeoutId);
      signal?.removeEventListener("abort", onExternalAbort);
    }
  },
};
